import React, { useState, useEffect, useRef } from 'react';
import './ShareModal.css';

/**
 * ShareModal - Beautiful modal for sharing and saving configurations
 */

export default function ShareModal({
  isOpen,
  onClose,
  shareableURL,
  currentConfig,
  savedConfigs = [],
  onSaveConfig,
  onLoadConfig,
  onDeleteConfig,
  onCopyLink,
  // Legacy prop names: several builders pass the raw useConfigShare handlers
  // under these names instead of the on* names above. Accepting both keeps
  // every builder working no matter which convention it was written against.
  saveNamedConfig,
  loadConfig,
  deleteNamedConfig,
  copyToClipboard,
  copySuccess,
  gunName,
  lightName,
  colorName,
}) {
  const [activeTab, setActiveTab] = useState('share');
  const [saveName, setSaveName] = useState('');
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const modalRef = useRef(null);
  const inputRef = useRef(null);

  // Resolve each handler from whichever prop name the parent builder used.
  const saveHandler = onSaveConfig || saveNamedConfig;
  const loadHandler = onLoadConfig || loadConfig;
  const deleteHandler = onDeleteConfig || deleteNamedConfig;
  const copyHandler = onCopyLink || copyToClipboard;

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.select(), 100);
    }
  }, [isOpen]);

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Handle save
  const handleSave = () => {
    if (typeof saveHandler !== 'function') return;
    const name = saveName.trim() || `${gunName || 'Custom'} Build`;
    saveHandler(currentConfig, name);
    setSaveName('');
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 2000);
  };

  // Native share API
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My ANR Design Holster Build',
          text: `Check out my custom ${gunName || ''} holster configuration!`,
          url: shareableURL,
        });
      } catch (e) {
        // User cancelled or error
      }
    }
  };

  // Format date
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="share-modal-backdrop" onClick={handleBackdropClick}>
      <div className="share-modal" ref={modalRef}>
        {/* Header */}
        <div className="share-modal-header">
          <h2>Save & Share Your Build</h2>
          <button className="share-modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Current Config Summary */}
        <div className="share-modal-summary">
          <div className="summary-item">
            <span className="summary-label">Firearm</span>
            <span className="summary-value">{gunName || 'Not selected'}</span>
          </div>
          {lightName && (
            <div className="summary-item">
              <span className="summary-label">Light</span>
              <span className="summary-value">{lightName}</span>
            </div>
          )}
          <div className="summary-item">
            <span className="summary-label">Color</span>
            <span className="summary-value">{colorName || 'Not selected'}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="share-modal-tabs">
          <button
            className={`tab-btn ${activeTab === 'share' ? 'active' : ''}`}
            onClick={() => setActiveTab('share')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" />
            </svg>
            Share Link
          </button>
          <button
            className={`tab-btn ${activeTab === 'save' ? 'active' : ''}`}
            onClick={() => setActiveTab('save')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
              <polyline points="17,21 17,13 7,13 7,21" />
              <polyline points="7,3 7,8 15,8" />
            </svg>
            My Builds
            {savedConfigs.length > 0 && (
              <span className="tab-badge">{savedConfigs.length}</span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className="share-modal-content">
          {activeTab === 'share' && (
            <div className="share-tab">
              {/* Copy Link Section */}
              <div className="share-section">
                <label className="share-label">Shareable Link</label>
                <div className="share-input-group">
                  <input
                    ref={inputRef}
                    type="text"
                    value={shareableURL || ''}
                    readOnly
                    className="share-input"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    className={`share-copy-btn ${copySuccess ? 'success' : ''}`}
                    onClick={() => copyHandler && copyHandler(shareableURL)}
                  >
                    {copySuccess ? (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20,6 9,17 4,12" />
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                        </svg>
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="share-hint">
                  Anyone with this link can view your exact configuration
                </p>
              </div>

              {/* Social Share Buttons */}
              <div className="share-section">
                <label className="share-label">Share Via</label>
                <div className="share-buttons">
                  {navigator.share && (
                    <button className="share-btn native" onClick={handleNativeShare}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="18" cy="5" r="3" />
                        <circle cx="6" cy="12" r="3" />
                        <circle cx="18" cy="19" r="3" />
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                      </svg>
                      Share
                    </button>
                  )}
                  <a
                    href={`sms:?body=Check out my ANR holster build: ${shareableURL}`}
                    className="share-btn sms"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                    </svg>
                    Text
                  </a>
                  <a
                    href={`mailto:?subject=My ANR Holster Build&body=Check out my custom holster configuration: ${shareableURL}`}
                    className="share-btn email"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    Email
                  </a>
                </div>
              </div>

              {/* Quick Save */}
              <div className="share-section save-quick">
                <label className="share-label">Save This Build</label>
                <div className="save-input-group">
                  <input
                    type="text"
                    placeholder="Name your build (e.g., 'EDC Setup')"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                    className="save-input"
                    maxLength={30}
                  />
                  <button
                    className={`save-btn ${showSaveSuccess ? 'success' : ''}`}
                    onClick={handleSave}
                  >
                    {showSaveSuccess ? 'Saved!' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'save' && (
            <div className="saves-tab">
              {savedConfigs.length === 0 ? (
                <div className="saves-empty">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                    <polyline points="17,21 17,13 7,13 7,21" />
                    <polyline points="7,3 7,8 15,8" />
                  </svg>
                  <p>No saved builds yet</p>
                  <span>Save your current configuration to access it later</span>
                </div>
              ) : (
                <div className="saves-list">
                  {savedConfigs.map((saved) => (
                    <div key={saved.id} className="saved-config-card">
                      <div className="saved-config-info">
  {/* SAFETY FIX: Check if name is a string. If it's an object, show fallback text. */}
  <h4>
    {typeof saved.name === 'string' ? saved.name : 'Unnamed Build (Data Error)'}
  </h4>
  <span className="saved-config-date">
    Saved {formatDate(saved.createdAt)}
  </span>
</div>
                      <div className="saved-config-actions">
                        <button
                          className="load-btn"
                          onClick={() => {
                            if (loadHandler) loadHandler(saved); // full object; builder unwraps .config
                            onClose();
                          }}
                        >
                          Load
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => deleteHandler && deleteHandler(saved.id)}
                          title="Delete"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3,6 5,6 21,6" />
                            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Save New from this tab */}
              <div className="saves-add-new">
                <div className="save-input-group">
                  <input
                    type="text"
                    placeholder="Name this build..."
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                    className="save-input"
                    maxLength={30}
                  />
                  <button
                    className={`save-btn ${showSaveSuccess ? 'success' : ''}`}
                    onClick={handleSave}
                  >
                    {showSaveSuccess ? 'Saved!' : '+ Save Current'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}